"use client";

import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as React from "react";
import { cn } from "@/lib/utils";

export interface SwitchProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    "onChange" | "value"
  > {
  label?: string;
  description?: string;
  labelPosition?: "left" | "right";
  /** @deprecated rsuite Toggle compat — use checked */
  value?: string;
  /** @deprecated rsuite Toggle compat — use onCheckedChange */
  onChange?: (checked: boolean) => void;
  /** @deprecated rsuite Toggle compat */
  checkedChildren?: React.ReactNode;
  /** @deprecated rsuite Toggle compat */
  unCheckedChildren?: React.ReactNode;
  /** @deprecated rsuite Toggle compat */
  loading?: boolean;
  /** @deprecated rsuite Toggle compat — ignored */
  size?: string;
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(
  (
    {
      label,
      description,
      labelPosition = "right",
      id,
      value: _value,
      onChange,
      onCheckedChange,
      checkedChildren: _checkedChildren,
      unCheckedChildren: _unCheckedChildren,
      loading: _loading,
      size: _size,
      className,
      ...props
    },
    ref
  ) => {
  const handleCheckedChange = onCheckedChange ?? onChange;
  const generatedId = React.useId();
  const switchId = id ?? generatedId;

  const switchEl = (
    <SwitchPrimitive.Root
      ref={ref}
      id={switchId}
      className={cn(
        "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-slate-300/70 bg-white/70 transition-colors duration-200 outline-none dark:border-zinc-800 dark:bg-zinc-950/70",
        "focus-visible:ring-2 focus-visible:ring-primary/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-zinc-700 dark:data-[state=unchecked]:border-zinc-600",
        className
      )}
      onCheckedChange={handleCheckedChange}
      {...props}
    >
      <SwitchPrimitive.Thumb className="pointer-events-none block h-5 w-5 rounded-full bg-background dark:bg-zinc-200 shadow-[0_1px_3px_rgba(15,23,42,0.22)] ring-0 transition-transform duration-200 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0 dark:shadow-[0_1px_3px_rgba(0,0,0,0.45)]" />
    </SwitchPrimitive.Root>
  );

  if (!label && !description) return switchEl;

  return (
    <div
      className={cn(
        "flex items-center gap-3",
        labelPosition === "left" && "flex-row-reverse justify-end"
      )}
    >
      {switchEl}
      <div className="flex flex-col gap-0.5">
        {label && (
          <label
            htmlFor={switchId}
            className="cursor-pointer select-none text-sm font-medium text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
          </label>
        )}
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
  }
);

Switch.displayName = "Switch";

export { Switch };
